import { useEffect, useMemo, type FC } from 'react';
import {
  Button,
  Dropdown,
  Slider,
  Tag,
  TextInput,
  Toggletip,
  ToggletipButton,
  ToggletipContent,
} from '@carbon/react';
import { Add, Information, TrashCan } from '@carbon/icons-react';
import { FormattedCount } from 'components/formatted-count';
import { PctInput } from './pct-input';
import type { CarryGroup, CarryWell, InterestItem } from './types';
import {
  CARRY_TRIGGERS,
  CARRY_TRIGGER_BY_ID,
  DEFAULT_PAYOUT_BASIS,
  PAYOUT_BASES,
  triggerHasBasis,
  carryTermsFromWells,
  deriveCarryDirection,
  thresholdForTrigger,
} from './interest-modeling-carry';
import type { WorkingConfigForm } from './hooks';
import { PropertyPicker } from './property-picker';
import styles from './carry-editor.module.scss';

/** The threshold slider's props (the subset used here). */
interface ThresholdSliderProps {
  id: string;
  labelText: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (data: { value: number | string }) => void;
}

/**
 * Carbon's `Slider` is typed to return `ReactNode` (includes `undefined`), which
 * the repo's React 17 JSX types reject as a JSX element. Cast to a component over
 * the props we use — the runtime component is unchanged.
 */
const ThresholdSlider = Slider as unknown as FC<ThresholdSliderProps>;

/** Props for {@link CarryEditor}. */
export interface CarryEditorProps {
  /** The OGL carry item being edited (`kind === 'carry'`). */
  item: InterestItem;
  /** The tract's full well roster (from `useTractWells`); the multi-select options + names + unassigned base. */
  wells: CarryWell[];
  /** Whether the roster fetch has RESOLVED for this tract — gates the Group-1 seed until wells are known. */
  wellsResolved: boolean;
  /** Whether the roster fetch is in flight (drives the "loading wells" hint). */
  wellsLoading: boolean;
  /** The lifted working-config form — the carry group setters. */
  workingConfig: WorkingConfigForm;
}

/**
 * The OGL carry position editor (INS-10673) — the expanded tree-node
 * body for a `kind: 'carry'` item. It CAPTURES carry terms into the working
 * config; the carry cashflow math is the calculator's job (deferred). Renders:
 *
 * - Property groups: add (empty) / rename inline / remove (blocked at one
 *    remaining; a removed group's wells fall back to unassigned).
 * - Per-group well membership via a filterable multi-select — a well
 *    belongs to ≤ 1 group, so assigning it here pulls it from any sibling group.
 *  - The unassigned-well count + an "About property groups" info toggletip
 * explaining unassigned wells are valued as a full working interest.
 *  - BPO & APO WI/NRI percent inputs, with the direction (Carrying / Carried)
 * DERIVED from the WI split, never entered.
 *  - A payout-trigger dropdown (7 triggers) + a threshold slider whose
 *    range/step/default come from the trigger; changing the trigger resets the
 * threshold to that trigger's default.
 *
 * A freshly-added carry item arrives with no groups; once its wells resolve, a
 * single "Group 1" holding ALL of them is seeded. Seeding is idempotent, so
 * a reopened item (already carrying groups) is untouched.
 *
 * @param {CarryEditorProps} props - The carry item, its well roster + load state, and the working config.
 * @return {JSX.Element} The carry editor body.
 */
export function CarryEditor({
  item,
  wells,
  wellsResolved,
  wellsLoading,
  workingConfig,
}: CarryEditorProps) {
  const { tractId } = item;
  const groups = useMemo(() => item.groups ?? [], [item.groups]);
  const {
    seedCarryGroups,
    addCarryGroup,
    renameCarryGroup,
    removeCarryGroup,
    setCarryGroupWells,
    patchCarryGroupTerms,
  } = workingConfig;

  // seed the initial, EMPTY "Group 1" once the roster resolves — the user builds
  // the grouping from the picker. `seedCarryGroups` is idempotent (no-op when groups
  // already exist), so a reopened item is never overwritten and a double-fire is
  // harmless.
  useEffect(() => {
    if (wellsResolved && groups.length === 0) {
      seedCarryGroups(tractId);
    }
  }, [wellsResolved, groups.length, tractId, seedCarryGroups]);

  // wells assigned to no group are "unassigned" (valued as a full working
  // interest). Counted over the roster, so a well referenced by a group but not
  // in the roster doesn't skew it.
  const assignedIds = useMemo(
    () => new Set(groups.flatMap((group) => group.wellIds)),
    [groups]
  );
  const unassignedCount = wells.filter(
    (well) => !assignedIds.has(well.id)
  ).length;

  return (
    <div className={styles.carryEditor}>
      <div className={styles.groupsHead}>
        <span className={styles.groupsTitleWrap}>
          <span className={styles.groupsTitle}>Property groups</span>
          <Toggletip align="bottom-start">
            <ToggletipButton label="About property groups">
              <Information />
            </ToggletipButton>
            <ToggletipContent>
              <p>
                Assign properties to groups; each group carries its own interest
                and payout metric. A property belongs to one group. Properties
                left unassigned are valued as a full working interest — full
                participation, no carry.
              </p>
            </ToggletipContent>
          </Toggletip>
        </span>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={Add}
          onClick={() => addCarryGroup(tractId)}
        >
          Add group
        </Button>
      </div>

      {wells.length > 0 && (
        <span className={styles.unassignedHint}>
          <FormattedCount value={unassignedCount} />
          {unassignedCount === 1 ? ' property' : ' properties'} unassigned
        </span>
      )}
      {wellsLoading && groups.length === 0 && (
        <span className={styles.wellsLoading}>Loading properties…</span>
      )}

      {groups.map((group) => (
        <GroupCard
          key={group.id}
          tractId={tractId}
          group={group}
          groups={groups}
          wells={wells}
          canRemove={groups.length > 1}
          renameCarryGroup={renameCarryGroup}
          removeCarryGroup={removeCarryGroup}
          setCarryGroupWells={setCarryGroupWells}
          patchCarryGroupTerms={patchCarryGroupTerms}
        />
      ))}
    </div>
  );
}

/** Props for one {@link GroupCard} (a single well group within a carry position). */
interface GroupCardProps {
  tractId: string;
  group: CarryGroup;
  /** Every group on the tract — a property already in a sibling is off the menu. */
  groups: CarryGroup[];
  wells: CarryWell[];
  /** False when this is the last group (removal is blocked — ≥ 1 group invariant). */
  canRemove: boolean;
  renameCarryGroup: WorkingConfigForm['renameCarryGroup'];
  removeCarryGroup: WorkingConfigForm['removeCarryGroup'];
  setCarryGroupWells: WorkingConfigForm['setCarryGroupWells'];
  patchCarryGroupTerms: WorkingConfigForm['patchCarryGroupTerms'];
}

/**
 * One well group card: name (inline rename), derived direction tag, remove
 * control, well multi-select, BPO/APO term inputs, and the payout metric +
 * threshold. Extracted so each group owns its own selected-wells memo.
 *
 * @param {GroupCardProps} props - The group + its tract, roster, and the setters.
 * @return {JSX.Element} The group card.
 */
function GroupCard({
  tractId,
  group,
  groups,
  wells,
  canRemove,
  renameCarryGroup,
  removeCarryGroup,
  setCarryGroupWells,
  patchCarryGroupTerms,
}: GroupCardProps) {
  const direction = deriveCarryDirection(group.bpoWi, group.apoWi);
  // Typed as PropertyOption[] (a well IS a PropertyOption — isSelectAll is optional) so
  // the multi-select's item type covers the select-all sentinel too.
  const triggerMeta = CARRY_TRIGGER_BY_ID[group.trigger];
  /**
   * Apply an accepted selection. The picker has already guaranteed the set is
   * homogeneous, so these terms are our real interest in every well the group now
   * holds. Absent only when the wells carry no certified figure, in which case the
   * existing terms stand rather than being zeroed.
   *
   * @param {CarryWell[]} next - The wells the group now holds.
   * @return {void}
   */
  const applyWells = (next: CarryWell[]): void => {
    setCarryGroupWells(
      tractId,
      group.id,
      next.map((well) => well.id)
    );
    const terms = carryTermsFromWells(next);
    if (terms) {
      patchCarryGroupTerms(tractId, group.id, terms);
    }
  };

  return (
    <div className={styles.groupCard}>
      <div className={styles.groupCardHead}>
        <TextInput
          id={`${group.id}-name`}
          labelText="Group name"
          hideLabel
          size="sm"
          value={group.name}
          onChange={(event) =>
            renameCarryGroup(tractId, group.id, event.target.value)
          }
        />
        {/* Direction is DERIVED from the WI split, so an empty group — which has no
          APO half yet — has no direction to state. No tag beats a guessed one. */}
        {direction !== null && (
          <Tag type={direction === 'carrying' ? 'gray' : 'teal'} size="sm">
            {direction === 'carrying' ? 'Carrying' : 'Carried'}
          </Tag>
        )}
        <Button
          hasIconOnly
          // Carbon routes a hasIconOnly Button through IconButton, whose `kind`
          // does not accept the `danger--*` variants — the TrashCan icon + label
          // carry the destructive intent instead.
          kind="ghost"
          size="sm"
          renderIcon={TrashCan}
          iconDescription="Remove group"
          tooltipPosition="left"
          disabled={!canRemove}
          onClick={() => removeCarryGroup(tractId, group.id)}
        />
      </div>

      <div className={styles.groupSection}>
        <PropertyPicker
          group={group}
          groups={groups}
          wells={wells}
          onChange={applyWells}
        />
      </div>

      <div className={styles.termsGrid}>
        <div className={styles.termsCol}>
          <span className={styles.sectionLabel}>Before payout (BPO)</span>
          <PctInput
            id={`${group.id}-bpowi`}
            label="WI %"
            ariaLabel="Before payout WI %"
            value={group.bpoWi}
            onChange={(value) =>
              patchCarryGroupTerms(tractId, group.id, { bpoWi: value })
            }
          />
          <PctInput
            id={`${group.id}-bponri`}
            label="NRI %"
            ariaLabel="Before payout NRI %"
            value={group.bpoNri}
            onChange={(value) =>
              patchCarryGroupTerms(tractId, group.id, { bpoNri: value })
            }
          />
        </div>
        <div className={styles.termsCol}>
          <span className={styles.sectionLabel}>After payout (APO)</span>
          <PctInput
            id={`${group.id}-apowi`}
            label="WI %"
            ariaLabel="After payout WI %"
            value={group.apoWi}
            onChange={(value) =>
              patchCarryGroupTerms(tractId, group.id, { apoWi: value })
            }
          />
          <PctInput
            id={`${group.id}-aponri`}
            label="NRI %"
            ariaLabel="After payout NRI %"
            value={group.apoNri}
            onChange={(value) =>
              patchCarryGroupTerms(tractId, group.id, { apoNri: value })
            }
          />
        </div>
      </div>

      <div className={styles.groupSection}>
        <span className={styles.sectionLabel}>Payout metric</span>
        <Dropdown
          id={`${group.id}-trigger`}
          size="sm"
          titleText=""
          label="Payout metric"
          items={CARRY_TRIGGERS}
          itemToString={(meta) => meta?.label ?? ''}
          selectedItem={triggerMeta}
          onChange={({ selectedItem }) => {
            if (!selectedItem) {
              return;
            }
            // a trigger change snaps the threshold to that trigger's default.
            patchCarryGroupTerms(tractId, group.id, {
              trigger: selectedItem.id,
              threshold: thresholdForTrigger(selectedItem.id),
            });
          }}
        />
        {/* The basis a recovery trigger measures its multiple against (INS-10673).
          Hidden for triggers that open no payout account. */}
        {triggerHasBasis(group.trigger) && (
          <div className={styles.thresholdField}>
            <Dropdown
              id={`${group.id}-basis`}
              size="sm"
              titleText="Payout measured on"
              label="Payout basis"
              items={PAYOUT_BASES}
              itemToString={(meta) => meta?.label ?? ''}
              selectedItem={
                PAYOUT_BASES.find(
                  (meta) =>
                    meta.id === (group.payoutBasis ?? DEFAULT_PAYOUT_BASIS)
                ) ?? PAYOUT_BASES[0]
              }
              helperText={
                PAYOUT_BASES.find(
                  (meta) =>
                    meta.id === (group.payoutBasis ?? DEFAULT_PAYOUT_BASIS)
                )?.help
              }
              onChange={({ selectedItem }) => {
                if (!selectedItem) {
                  return;
                }
                patchCarryGroupTerms(tractId, group.id, {
                  payoutBasis: selectedItem.id,
                });
              }}
            />
          </div>
        )}

        {triggerMeta.hasValue && (
          <div className={styles.thresholdField}>
            <ThresholdSlider
              id={`${group.id}-threshold`}
              labelText={`Threshold (${triggerMeta.unit ?? ''})`}
              min={triggerMeta.min ?? 0}
              max={triggerMeta.max ?? 100}
              step={triggerMeta.step ?? 1}
              value={group.threshold}
              onChange={({ value }) =>
                patchCarryGroupTerms(tractId, group.id, {
                  threshold: typeof value === 'number' ? value : Number(value),
                })
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}
